/**
 * BrandMark — FLEX ENERGY UNLIMITED corporate logo.
 *
 * Modernised SVG version of the brand logo (white-outlined amber disc with a
 * sharp lightning bolt). Drawn from primitives so it scales crisply at any
 * size and adapts to any background.
 *
 * Variants:
 *   - default     navy circle bg + amber disc + white bolt   (use on light bgs)
 *   - inverse     white circle bg + amber disc + navy bolt   (rare)
 *   - transparent NO background, only ring + disc + bolt     (use on dark bgs)
 *   - mark        just the amber disc + bolt, no outer navy  (compact)
 */
export function BrandMark({ size = 36, variant = "default", className = "" }) {
  const NAVY = "#0B1F3A";
  const AMBER = "#FBBF24";
  const AMBER_DEEP = "#F59E0B";
  const WHITE = "#FFFFFF";

  const bgFill =
    variant === "inverse" ? WHITE : variant === "transparent" || variant === "mark" ? "none" : NAVY;
  // White ring is meaningful only when there is a contrasting dark disc behind it.
  // On transparent or mark variants the ring becomes an awkward floating outline,
  // so we suppress it.
  const ringStroke =
    variant === "inverse" ? NAVY :
    variant === "default" ? WHITE : "none";
  const boltFill = variant === "inverse" ? NAVY : WHITE;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      className={`brand-mark ${className}`}
      aria-label="FLEX ENERGY UNLIMITED"
      role="img"
    >
      {/* Outer navy disc (skipped on transparent/mark variants) */}
      {bgFill !== "none" && <circle cx="32" cy="32" r="31" fill={bgFill} />}

      {/* Optional subtle inner shadow / gradient for depth on filled variant */}
      {variant === "default" && (
        <circle
          cx="32"
          cy="32"
          r="31"
          fill="url(#bm-depth)"
          opacity="0.35"
        />
      )}

      {/* Amber disc */}
      <circle cx="32" cy="32" r="20" fill={AMBER} className="brand-mark__disc" />
      <circle cx="32" cy="32" r="20" fill="url(#bm-amber-grad)" opacity="0.5" className="brand-mark__disc" />

      {/* White ring around the amber disc */}
      {ringStroke !== "none" && (
        <circle
          cx="32"
          cy="32"
          r="22.5"
          fill="none"
          stroke={ringStroke}
          strokeWidth="2"
          className="brand-mark__ring"
        />
      )}

      {/* Sharper, more aggressive lightning bolt — Tesla-inspired geometry */}
      <path
        d="M37 12 L20 36 L28.5 36 L25 52 L46 26 L36 26 L40 12 Z"
        fill={boltFill}
        stroke={boltFill}
        strokeWidth="0.5"
        strokeLinejoin="round"
        className="brand-mark__bolt"
      />

      <defs>
        <radialGradient id="bm-depth" cx="50%" cy="50%" r="50%">
          <stop offset="60%" stopColor="#000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.6" />
        </radialGradient>
        <radialGradient id="bm-amber-grad" cx="35%" cy="30%" r="70%">
          <stop offset="0%" stopColor={AMBER} stopOpacity="0" />
          <stop offset="100%" stopColor={AMBER_DEEP} stopOpacity="1" />
        </radialGradient>
      </defs>
    </svg>
  );
}

export function BrandLockup({ size = 36, subtitle = "Texas · Dashboard", variant = "default" }) {
  const isDark = variant === "dark";
  // On dark sidebar, transparent variant lets navy bg show through cleanly
  const markVariant = isDark ? "transparent" : variant;
  return (
    <div className="flex items-center gap-3">
      <BrandMark size={size} variant={markVariant} />
      <div>
        <div className={`text-sm font-semibold leading-none tracking-tight ${isDark ? "text-white" : "text-slate-900 dark:text-white"}`}>FLEX ENERGY</div>
        <div className={`text-[10px] uppercase tracking-[0.2em] mt-1 ${isDark ? "text-amber-300/80" : "text-slate-500 dark:text-slate-400"}`}>{subtitle}</div>
      </div>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="mt-12 border-t border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 transition-colors" data-testid="app-footer">
      <div className="max-w-[1600px] mx-auto px-8 py-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[11px] text-slate-500 dark:text-slate-400">
        <div className="flex items-center gap-3">
          <BrandMark size={22} />
          <div>
            <div className="font-semibold text-slate-700 dark:text-slate-200">FLEX ENERGY</div>
            <div className="text-[10px] text-slate-500 dark:text-slate-400">Energy Brokerage · Texas, USA</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
          <a href="mailto:support@flexenergy.pro" className="hover:text-slate-900 dark:hover:text-white transition-colors">support@flexenergy.pro</a>
          <span className="text-slate-300 dark:text-slate-600">·</span>
          <span>© {new Date().getFullYear()} FLEX ENERGY</span>
          <span className="text-slate-300 dark:text-slate-600">·</span>
          <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">v1.0.0</span>
        </div>
      </div>
    </footer>
  );
}
